import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Loader2, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const LIGHT_THEME = {
  "--ink": "#111111",
  "--ink-soft": "#666666",
  "--accent": "#0f3d32",
  "--accent-light": "#eaf0ee",
  "--hairline": "rgba(17,17,17,0.08)",
  background: "#f4f3f1",
  color: "#111111",
  fontFamily: "'Inter', system-ui, sans-serif",
  minHeight: "100vh",
};

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing reset token — request a new link from the login page.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated — redirecting to sign in");
      setTimeout(() => nav("/admin", { replace: true }), 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="relative z-[2] flex min-h-screen items-center justify-center px-6" style={LIGHT_THEME}>
      <div className="w-full max-w-sm rounded-2xl border border-[var(--hairline)] bg-white p-7 shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--accent-light)] text-[var(--accent)]">
          {done ? <Check size={22} /> : <Lock size={20} />}
        </div>
        {done ? (
          <>
            <h1 className="text-2xl font-bold">Password updated</h1>
            <p className="mt-2 text-sm text-[var(--ink-soft)]">Redirecting you to sign in with your new password…</p>
          </>
        ) : (
          <form onSubmit={submit}>
            <h1 className="text-2xl font-bold">Set a new password</h1>
            <p className="mb-5 text-sm text-[var(--ink-soft)]">Choose a strong password (at least 8 characters).</p>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">New password</Label>
                <Input data-testid="reset-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Confirm password</Label>
                <Input data-testid="reset-pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8} />
              </div>
              <button
                type="submit"
                disabled={loading || !token}
                data-testid="reset-submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--accent)] py-3 text-sm font-semibold text-white disabled:opacity-60"
              >
                {loading ? <Loader2 size={15} className="animate-spin" /> : null}
                {loading ? "Saving…" : "Update password"}
              </button>
              <button
                type="button"
                onClick={() => nav("/admin")}
                className="w-full text-center text-xs text-[var(--ink-soft)] hover:text-[var(--ink)]"
              >
                Back to sign in
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
